import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cur } from '@/lib/currency';

const STATUS_MAP = { draft: 'مسودة', sent: 'مرسل', sale: 'مؤكد', cancel: 'ملغي', done: 'منتهي' };
const STATUS_COLOR = {
  draft: 'bg-amber-50 text-amber-700 border border-amber-200',
  sent: 'bg-blue-50 text-blue-700 border border-blue-200',
  sale: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  cancel: 'bg-red-50 text-red-700 border border-red-200',
  done: 'bg-gray-100 text-gray-600 border border-gray-200',
};

const FILTERS = [
  { key: 'all', label: 'الكل' },
  { key: 'sale', label: 'مؤكد' },
  { key: 'draft', label: 'مسودة' },
  { key: 'cancel', label: 'ملغي' },
];

export default function OrdersTab({ orders, ordersTotal, customerId, customerName }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? orders : orders.filter(o => o.state === filter);
  const totalValue = orders.filter(o => ['sale', 'done'].includes(o.state))
    .reduce((s, o) => s + (o.amount_total || 0), 0);

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-3 text-center">
          <p className="text-2xl font-bold text-primary">{orders.length}</p>
          <p className="text-xs text-muted-foreground">إجمالي الطلبات</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 text-center">
          <p className="text-lg font-bold text-emerald-700">{totalValue.toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur()}</p>
          <p className="text-xs text-muted-foreground">إجمالي المؤكد</p>
        </div>
      </div>

      {/* New order button */}
      <Button
        onClick={() => navigate(`/orders/new?customer_id=${customerId}&customer_name=${encodeURIComponent(customerName)}`)}
        className="w-full bg-primary gap-2"
      >
        <Plus className="w-4 h-4" /> طلب جديد
      </Button>

      {/* Filter chips */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${
              filter === f.key ? 'bg-primary text-white' : 'bg-card border border-border text-muted-foreground'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Orders list */}
      {filtered.length === 0 ? (
        <p className="text-center text-muted-foreground py-10 text-sm">لا توجد طلبات</p>
      ) : (
        <div className="space-y-2">
          {filtered.map(order => (
            <div key={order.id}
              onClick={() => navigate(`/orders/${order.id}`)}
              className="bg-card border border-border rounded-2xl p-4 cursor-pointer hover:shadow-md hover:border-primary/30 transition-all"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-primary flex-shrink-0" />
                    <p className="font-bold text-foreground">{order.name}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 mr-6">
                    {new Date(order.date_order).toLocaleDateString('ar-EG', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <div className="text-left flex-shrink-0 flex flex-col items-end gap-1">
                  <p className="font-bold text-foreground">{Number(order.amount_total).toLocaleString('ar-EG', { maximumFractionDigits: 0 })} {cur()}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[order.state] || 'bg-muted text-muted-foreground'}`}>
                    {STATUS_MAP[order.state] || order.state}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}